
import React from "react";

import API from "@dndUtilities/system_api";
import InfoButton from "@components/input/buttons/InfoButton";

const api = API.getInstance();

export default function Home() {
  return (
    <div>
      <div className="header centered" style={{ paddingTop: "4px" }}>Welcome to Homebrew Labs</div>
      <div className="small_label gray centered">
        User friendly dynamic tool for tracking everything you need in D&D 
      </div>
      <div className="small_label gray centered">
        for your Characters, Campaigns, and even your own Homebrew Creations
      </div>
      <p>
        This is my system for keeping track of all things D&D.
      </p>
      <p>
        There's a menu button in the top left which can be used to access all kinds of things. 
        If you're not logged in, then it will only have Source Books, Game Rules, and Information.
      </p>
      <p>
        I'll go into those in a second. You are allowed to see them when not logged in, but for most users, 
        you're going to be more interested in what you can do while logged in, so let's get to that first.
      </p>
      <p>
        In the top right is the Login/Register button. 
        You'll need to register and login before you can access and make use of the other pages, which is where the focus of the
        entire website is.
      </p>
      <p>
        Once you're logged in, the menu will now also have a My Content section, 
        which includes My Campaigns and My Characters. Clicking on each of them will take you to pages where you can see your 
        campaigns or characters, as well as create new ones.
        Any Source Books you have access to (which currently is just the free ones) will have their content available when 
        creating characters and campaigns.
      </p>
      <p>
        The My Characters page allows you to make your own characters, but you can also make characters as part of a campaign. 
        Characters made in this way will also show up on your My Characters page, but as part of a campaign, they will have access 
        to a number of nice features that solo characters do not. 
        The page for each campaign also has tools for adding players and characters to the campaign.
      </p>
      <p>
        One thing to note on both the page for a character and for a campaign is the Options tab. 
        This allows the owner of the character or the campaign to set a number of options for it. 
        The options to choose from are the same for both, and characters which are part of a campaign automatically inherit these 
        options from the campaign. The Options tab has 3 sub sections: Allowed Sources, Blocked Options, and Special Rules. 
        By default all of the official Source Books available to you are allowed in the Allowed Sources, 
        but you may find it useful to remove some. 
        Third Party and Unearthed Arcana source books may be available to you, 
        but by default they are set as not allowed in Character Options. 
        The Special Rules section will have lots of things that can be turned on or off, or set to specific options. 
        Feel free to explore it. We set it up to automatically use the options we think most users would like.
        At the top of the Special Rules section though are options to allow UA Content and 3rd Party Content. 
        Setting these to On will make Unearthed Arcana and 3rd Party source books show up in the Allowed Sources section, 
        and they can then be enabled one by one. 
        We suggest you try allowing 3rd Party, and taking a look at the 3rd Party content we have available. 
        Currently it's only things we either made ourselves, or which we found from a free source on the internet, 
        but we hope to add more options in the future. 
      </p>
      <p>
        We've done our best to make all of our tools as intuitive and easy as possible, 
        and we've also automated a lot of things that other similar tools do not. 
        I'm particularly proud of how Summon effects like Find Familiar and Transform effects like Wild Shape turned out.
        Those are by far the most complicated mechanically, which also makes them harder for GMs and Players to use them, 
        so tools to help with them are crucial.
      </p>
      <p>
        The campaign tools for GMs are also particularly nice. On top of tracking the players and characters in the campaign 
        (which is also visible for the players), GMs also get an Encounter Builder, areas for keeping track of NPCs, other notes,
        and I'm working on an area for quickly looking up rules, all without leaving the page so it doesn't have to 
        reload character or creature information. 
        Think of it as a Digital GM Screen. When I'm running a campaign I pretty much never leave that page for anything 
        other than to access a Virtual Table Top.
      </p>
      <p>
        Also while you're logged in there will be a Chat bubble in the bottom right of the page which can be clicked to pull 
        up chat tools. If you're in any campaigns you will be able to chat with the other members of your campaign, 
        but whether or not you are in any campaigns, you will be able to chat with the System Admin (currently that's just me).
        I'm happy to help with any questions you have, 
        or if you run into any bugs or have suggestions of how to improve the website, feel free to reach out.
      </p>
      <p>
        That's the basics of what most of you are going to be interested in, 
        other than maybe reading source books.
      </p>
      <p>Now I'll go over what's in the other pages.</p>
      <p>
        Source Books has a list of all the Source Books currently in the system. 
        For legal reasons I currently only have "books" that are made available for free. 
        Hopefully we can expand it to have additional books in the near future.
        Some of them can be clicked and have their content available to read right there, 
        but I haven't gotten all of the content for all of them in a readable format yet. 
        I do however have their content available for use in other parts of the website.
        The process to make them readable takes a bit, but I'll get to all of them eventually. 
      </p>
      <p>
        Currently SRD 5.1 is readable. 
        You may find that some of the things are ordered or otherwise organized differently from the official SRD 5.1, 
        but I think this way makes more sense, and it automatically puts it some parts of it together from what I have in my db
        for it.
      </p>
      <p>
        One of the Source Books is called Mad Homebrews. 
        This is where I put things I've made myself and am making freely available for anyone who uses the website to use.
        It's not currently in a readable format, but I'll get the current contents of it made readable after I get SRD 5.2.1 readable.
      </p>
      <p>
        Once we have some non-free Source Books available for purchase, the page for the Source Book will provide the means to do so.
      </p>
      <p>
        The Game Rules menu option opens up to show all kinds of other things that are tracked in the system. 
        Everything from Races/Species, Backgrounds, Classes and Subclasses to Items, Creatures, 
        and terms that you probably aren't familiar with like "Magic Item Keyword" and "Special Feature". 
        The ones you're unfamiliar with probably will have some information about what the term means at the top of the list that comes up 
        for it if you click it in the menu. The information can be hidden and shown again by clicking the <InfoButton /> symbol 
        next to the name of the term on its page. 
        I separated different ideas within D&D in these ways because it made it easier to get it to all come together properly 
        when making characters, campaigns, encounters, etc.
        On any of the pages listing the various things that we have in our system, 
        you can click the name to see more information about it. 
        For some of them this information may be pretty light, but we're working on that. 
        There's also times where you may see information duplcated. We're working on that as well.
        For others it's already working really nice.
        The goal for the majority of the development time that went into building this website 
        was to make the character and campaign pages as useful as possible, 
        and making the tools for adding in additional content as easy and useful as possible as well.
      </p>
      <p>
        The information section is boring. A lot of it is stuff that we have to have on the website for legal purposes.
      </p>
      <p>
        Other than that, I'm planning to make third party books available for purchase on here. 
        Once that is done, they will show up in the list of Source Books, but they won't be readable or have their content usable
        until you pay for the book.
      </p>
      <p>
        Also, there's a Bug Icon in the top bar, next to the Login/Register button. 
        That can be used to report bugs or make requests. We will try to resolve any issues resolved as soon as possible, 
        but currently this is a one man operation, and I only work on it in my spare time. 
        That said, I did design it from the ground up with speed of development in mind, and I'm pretty good at what I do.
      </p>
      <p>
        Someday soon I'll put a video tutorial on this page to make it easier. 
        That will also allow me to actually show the various things in action.
      </p>
    </div>
  );
}
